// Money is integer cents in a bigint with an explicit currency (CLAUDE.md, domain rule 4).
//
// Nothing in this file uses a JavaScript number to hold an amount. A double cannot hold
// 0.1 exactly, so `0.1 + 0.2 !== 0.3`; a bill that is one cent out is a bill the cashier
// cannot reconcile at shift close and a claim SHA can reject. Cents are the smallest unit
// we ever owe, so bigint cents are exact and have no practical upper bound.
//
// The rounding and allocation rules below, and why they were chosen, are recorded in
// docs/adr/0006-money-and-rounding.md. Do not change them without updating that ADR.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';

/**
 * Currencies Wodi handles. Only Kenyan shillings today; the field exists so that an
 * amount can never be added to an amount in another currency by accident.
 */
export type Currency = 'KES';

export const KES = 'KES' satisfies Currency;

/** Cents in the currency's minor unit. KES 1 is 100 cents. */
export interface Money {
  readonly currency: Currency;
  readonly cents: bigint;
}

export type MoneyErrorCode =
  'money.empty' | 'money.not_a_number' | 'money.too_many_decimals' | 'money.unknown_currency';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const MONEY_ERRORS: Record<MoneyErrorCode, string> = {
  'money.empty': 'No amount was entered',
  'money.not_a_number': 'That is not an amount',
  'money.too_many_decimals': 'An amount has at most two decimal places',
  'money.unknown_currency': 'Only Kenyan shillings (KES) are supported',
};

// --- Construction -----------------------------------------------------------------

export function moneyFromCents(cents: bigint, currency: Currency = KES): Money {
  return { currency, cents };
}

/** KES 250 is `moneyFromShillings(250n)`. */
export function moneyFromShillings(shillings: bigint, currency: Currency = KES): Money {
  return { currency, cents: shillings * 100n };
}

export function zeroMoney(currency: Currency = KES): Money {
  return { currency, cents: 0n };
}

/**
 * Strict construction from a decimal string, for amounts written in code, configuration
 * or an import file: `'1200'`, `'1200.50'`, `'-75.25'`. No grouping separators, no
 * currency word, at most two decimal places. Throws on anything else, because these
 * sources are under our control. For what a person types, use `parseMoney`.
 */
export function moneyFromDecimalString(text: string, currency: Currency = KES): Money {
  const cents = decimalStringToMinorUnits(text);
  if (cents === null) throw new RangeError(`Not a decimal amount with at most 2 decimals: ${text}`);
  return { currency, cents };
}

/**
 * Converts a plain decimal string to minor units (hundredths) without ever touching a
 * float: the integer and fraction parts are concatenated as digits.
 * Returns null when the text is not a well-formed decimal with at most two decimals.
 */
function decimalStringToMinorUnits(text: string): bigint | null {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const hundredths = BigInt(whole + fraction.padEnd(2, '0'));
  return sign === '-' ? -hundredths : hundredths;
}

// --- Arithmetic -------------------------------------------------------------------
//
// These take amounts the domain has already built, so a currency mismatch is a bug in
// our code and throws rather than returning an error to show a user.

/**
 * The currency as a plain string. `Currency` has one member today, so the compiler folds
 * any comparison of two `Currency` values to a constant and the lint rules then reject
 * the guard as dead code. Reading the code as a string keeps the runtime check, which is
 * the check that matters on the day a second currency exists.
 */
function currencyCode(amount: Money): string {
  return amount.currency;
}

function assertSameCurrency(a: Money, b: Money): void {
  const left = currencyCode(a);
  const right = currencyCode(b);
  if (left !== right) throw new TypeError(`Cannot combine ${left} with ${right}`);
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { currency: a.currency, cents: a.cents + b.cents };
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { currency: a.currency, cents: a.cents - b.cents };
}

export function negateMoney(amount: Money): Money {
  return { currency: amount.currency, cents: -amount.cents };
}

export function absMoney(amount: Money): Money {
  return { currency: amount.currency, cents: amount.cents < 0n ? -amount.cents : amount.cents };
}

/**
 * Multiplies by a whole quantity — three tablets, two bed-days. Quantities are counts, so
 * they are integers; a fractional quantity (half a vial) has to be modelled as a smaller
 * unit or priced with `applyRate`, never as a float multiplier.
 */
export function multiplyMoney(amount: Money, quantity: bigint | number): Money {
  return { currency: amount.currency, cents: amount.cents * toIntegerBigInt(quantity, 'quantity') };
}

function toIntegerBigInt(value: bigint | number, label: string): bigint {
  if (typeof value === 'bigint') return value;
  if (!Number.isSafeInteger(value)) throw new RangeError(`${label} must be a whole number`);
  return BigInt(value);
}

export function sumMoney(amounts: readonly Money[], currency: Currency = KES): Money {
  const expected: string = currency;
  let cents = 0n;
  for (const amount of amounts) {
    const found = currencyCode(amount);
    if (found !== expected) throw new TypeError(`Cannot sum ${found} into ${expected}`);
    cents += amount.cents;
  }
  return { currency, cents };
}

// --- Rounding ---------------------------------------------------------------------

/**
 * Integer division rounded half away from zero: 2.5 becomes 3 and -2.5 becomes -3.
 *
 * This is the rule people in a cash office expect ("round the half up") and it is
 * symmetric, so a refund of a discounted line is the negative of the original line
 * rather than a cent out. Banker's rounding would be less surprising statistically but
 * more surprising at the counter.
 */
export function divideRoundedHalfAwayFromZero(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new RangeError('Division by zero');
  const numeratorIsNegative = numerator < 0n;
  const denominatorIsNegative = denominator < 0n;
  const negative = numeratorIsNegative !== denominatorIsNegative;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const quotient = n / d;
  const remainder = n % d;
  const magnitude = remainder * 2n >= d ? quotient + 1n : quotient;
  return negative ? -magnitude : magnitude;
}

/**
 * A rate held as basis points: hundredths of a percent, so 12.5% is 1250 and 100% is
 * 10000. An integer rate keeps percentages out of floating point too.
 */
export type BasisPoints = number;

export const ONE_HUNDRED_PERCENT: BasisPoints = 10_000;

/** `'12.5'` becomes 1250 basis points. Two decimal places of a percent is the limit. */
export function basisPointsFromPercent(percent: string): Result<BasisPoints, MoneyErrorCode> {
  const trimmed = percent.trim();
  if (trimmed === '') return err('money.empty');
  const value = decimalStringToMinorUnits(trimmed.endsWith('%') ? trimmed.slice(0, -1) : trimmed);
  if (value === null)
    return err(/^-?\d+\.\d{3,}$/.test(trimmed) ? 'money.too_many_decimals' : 'money.not_a_number');
  return ok(Number(value));
}

/**
 * Applies a rate to an amount, rounded half away from zero. Used for payer splits and
 * discounts.
 *
 * TODO(verify-spec): VAT and other KRA/eTIMS tax amounts must NOT be computed with this
 * until the eTIMS rounding rule (which line the tax is rounded on, and at what precision)
 * is confirmed against KRA's specification. See docs/integrations/.
 */
export function applyRate(amount: Money, rate: BasisPoints): Money {
  const cents = divideRoundedHalfAwayFromZero(
    amount.cents * toIntegerBigInt(rate, 'rate'),
    BigInt(ONE_HUNDRED_PERCENT),
  );
  return { currency: amount.currency, cents };
}

/**
 * `amount × numerator ÷ denominator`, rounded half away from zero once, at the end. For a
 * share that is a ratio of two amounts: the part of a charge a payment covered, or a fixed
 * fee spread over that part (docs/plans/phase3-accounting-and-revenue-share.md, R3).
 * Because the rounding is symmetric, scaling a negative amount gives exactly the negative of
 * scaling the positive one, so a reversal mirrors what it reverses.
 */
export function scaleMoney(amount: Money, numerator: bigint, denominator: bigint): Money {
  if (denominator <= 0n) throw new RangeError('The denominator must be positive');
  return {
    currency: amount.currency,
    cents: divideRoundedHalfAwayFromZero(amount.cents * numerator, denominator),
  };
}

export interface DiscountResult {
  /** What comes off, always the rounded value. */
  readonly discount: Money;
  /** What remains. `discount + net` is exactly the original amount. */
  readonly net: Money;
}

/**
 * A percentage discount. The discount is the rounded number and the net is the original
 * amount minus it, so the two always add back to the original exactly — no cent appears
 * or disappears when an invoice is totalled from its lines.
 *
 * Rates outside 0–100% throw: a negative discount is a surcharge and a discount over
 * 100% turns a bill into a credit, and both need their own reviewed operation with an
 * approver (CLAUDE.md, domain rule 10).
 */
export function applyDiscount(amount: Money, rate: BasisPoints): DiscountResult {
  if (!Number.isInteger(rate) || rate < 0 || rate > ONE_HUNDRED_PERCENT)
    throw new RangeError(`Discount rate must be 0 to ${String(ONE_HUNDRED_PERCENT)} basis points`);
  const discount = applyRate(amount, rate);
  return { discount, net: subtractMoney(amount, discount) };
}

// --- Allocation -------------------------------------------------------------------

/**
 * Splits a total across weighted shares without losing or inventing a cent: the returned
 * amounts always add up to the total exactly.
 *
 * Each share gets the truncated exact share, then the leftover cents go one each to the
 * shares with the largest truncated remainder (the largest-remainder method). Ties go to
 * the earlier share, so the result depends only on the inputs and their order — the same
 * invoice split twice produces the same numbers.
 *
 * Weights are counts or ratios, not money: `[1n, 1n, 1n]` splits evenly, `[8000n, 2000n]`
 * splits 80/20.
 */
export function allocateMoney(total: Money, weights: readonly bigint[]): Money[] {
  if (weights.length === 0) throw new RangeError('Allocation needs at least one share');
  let totalWeight = 0n;
  for (const weight of weights) {
    if (weight < 0n) throw new RangeError('Allocation weights cannot be negative');
    totalWeight += weight;
  }
  if (totalWeight === 0n) throw new RangeError('Allocation weights cannot all be zero');

  const magnitude = total.cents < 0n ? -total.cents : total.cents;
  const step = total.cents < 0n ? -1n : 1n;

  // Truncated share plus the remainder that decides who gets a leftover cent. Both are
  // computed on the magnitude so a negative total mirrors a positive one exactly.
  const shares = weights.map((weight, index) => ({
    index,
    cents: (magnitude * weight) / totalWeight,
    remainder: (magnitude * weight) % totalWeight,
  }));

  let leftover = magnitude;
  for (const share of shares) leftover -= share.cents;

  const byRemainder = [...shares].sort((a, b) =>
    a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
  );
  // The fractional parts of the exact shares sum to fewer than one share, so there are
  // always fewer leftover cents than shares and every leftover cent finds a home.
  const leftoverCents = Number(leftover);
  for (let i = 0; i < leftoverCents; i++) {
    const share = byRemainder[i];
    if (share) share.cents += 1n;
  }

  return shares.map((share) => ({ currency: total.currency, cents: share.cents * step }));
}

/** Splits a total into `parts` shares as evenly as cents allow. */
export function allocateMoneyEvenly(total: Money, parts: number): Money[] {
  if (!Number.isSafeInteger(parts) || parts < 1)
    throw new RangeError('Allocation needs at least one share');
  return allocateMoney(total, new Array<bigint>(parts).fill(1n));
}

// --- Comparison -------------------------------------------------------------------

/** Negative when a < b, zero when equal, positive when a > b. */
export function compareMoney(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  if (a.cents === b.cents) return 0;
  return a.cents < b.cents ? -1 : 1;
}

export function moneyEquals(a: Money, b: Money): boolean {
  return currencyCode(a) === currencyCode(b) && a.cents === b.cents;
}

export function isZeroMoney(amount: Money): boolean {
  return amount.cents === 0n;
}

export function isNegativeMoney(amount: Money): boolean {
  return amount.cents < 0n;
}

export function isPositiveMoney(amount: Money): boolean {
  return amount.cents > 0n;
}

export function minMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) <= 0 ? a : b;
}

export function maxMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) >= 0 ? a : b;
}

// --- Display ----------------------------------------------------------------------

export interface FormatMoneyOptions {
  /** Show `.00` on whole shillings too. Default false (DESIGN.md §9: `KES 12,500`). */
  readonly alwaysShowCents?: boolean;
  /** Include the `KES ` prefix. Default true. Turn it off for a numeric table column. */
  readonly showCurrency?: boolean;
}

/**
 * `KES 12,500`, `KES 12,500.75`, `-KES 500` (DESIGN.md §9: whole shillings carry no
 * decimals, negative amounts take a minus sign and are never coloured).
 *
 * The minus sign leads the whole string, as `Intl` does for currency in English, so a
 * column of amounts lines up on the digits. Grouping is done here rather than through
 * `Intl.NumberFormat` so the output cannot shift with the host's locale data — a printed
 * receipt and an eTIMS payload must read the same on every machine.
 */
export function formatMoney(amount: Money, options?: FormatMoneyOptions): string {
  const showCurrency = options?.showCurrency ?? true;
  const negative = amount.cents < 0n;
  const magnitude = negative ? -amount.cents : amount.cents;

  const shillings = magnitude / 100n;
  const cents = magnitude % 100n;
  const digits = groupThousands(shillings.toString());
  const withCents =
    cents !== 0n || options?.alwaysShowCents === true
      ? `${digits}.${cents.toString().padStart(2, '0')}`
      : digits;

  return `${negative ? '-' : ''}${showCurrency ? `${amount.currency} ` : ''}${withCents}`;
}

function groupThousands(digits: string): string {
  let out = '';
  for (let end = digits.length; end > 0; end -= 3)
    out = digits.slice(Math.max(0, end - 3), end) + (out === '' ? '' : `,${out}`);
  return out;
}

// --- Parsing what a cashier types --------------------------------------------------

const CURRENCY_WORDS = new Set(['KES', 'KSH', 'KSHS', 'SH', 'SHS']);

/**
 * Reads an amount a person typed. Accepts what actually gets typed at a counter:
 * `1200`, `1,200`, `1 200`, `KES 1,200`, `Ksh 1200.50`, `-500`, `(500)`.
 *
 * Grouping, if present, has to be well formed (`1,200`, not `1,20`), so a mistyped
 * separator is a question rather than a silent factor-of-ten error. Parentheses mean a
 * negative amount, the accounting convention on the receipts these clerks already use.
 * The sign is returned as typed; whether a negative amount is allowed at all is the
 * caller's rule.
 */
export function parseMoney(input: string, currency: Currency = KES): Result<Money, MoneyErrorCode> {
  const trimmed = input.trim();
  if (trimmed === '') return err('money.empty');

  let text = trimmed;
  let negative = false;
  if (text.startsWith('(') && text.endsWith(')')) {
    negative = true;
    text = text.slice(1, -1).trim();
  }

  // Accepted order is sign then currency (`-KES 500`), but `KES -500` is typed often
  // enough to be worth reading, so the currency word is taken off either side of the sign.
  const beforeSign = stripCurrencyWord(text);
  if (beforeSign === null) return err('money.unknown_currency');
  text = beforeSign;
  if (!negative && text.startsWith('-')) {
    negative = true;
    text = text.slice(1).trim();
  } else if (text.startsWith('+')) {
    text = text.slice(1).trim();
  }
  const afterSign = stripCurrencyWord(text);
  if (afterSign === null) return err('money.unknown_currency');
  text = afterSign.replace(/\s/g, '');

  if (text === '') return err('money.not_a_number');
  if (/^\d[\d,]*\.\d{3,}$/.test(text)) return err('money.too_many_decimals');
  if (!/^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?$/.test(text)) return err('money.not_a_number');

  const cents = decimalStringToMinorUnits(text.replace(/,/g, ''));
  if (cents === null) return err('money.not_a_number');
  return ok({ currency, cents: negative ? -cents : cents });
}

/**
 * Removes a leading currency word and any `.` or spaces after it. Returns the text
 * unchanged when there is no leading word, or null when the word names another currency.
 *
 * An unknown word only counts as a currency when digits follow it, so `USD 500` is
 * "wrong currency" while `abc` is simply not an amount.
 */
function stripCurrencyWord(text: string): string | null {
  const match = /^([A-Za-z]+)\.?\s*/.exec(text);
  if (!match) return text;
  const word = match[1] ?? '';
  const rest = text.slice(match[0].length);
  if (CURRENCY_WORDS.has(word.toUpperCase())) return rest;
  return /\d/.test(rest) ? null : text;
}
